import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { Alert, Button, Popconfirm, Select, Space, Table, Tag, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { PermissionGate } from "@/shared/components/PermissionGate";
import { HolidayFormModal } from "../components/HolidayFormModal";
import { useCreateHoliday, useDeleteHoliday, useHolidaysList } from "../hooks/useSchedules";
import type { Holiday, HolidayInput } from "../types";

const { Title } = Typography;

const YEAR_RANGE = 3;

/** Joriy yildan bir yil orqaga va bir yil oldinga. */
const yearOptions = Array.from({ length: YEAR_RANGE }, (_, index) => {
	const year = dayjs().year() - 1 + index;
	return { value: year, label: String(year) };
});

const emptyCell = <span className="text-gray-400">—</span>;

interface DeleteActionProps {
	holiday: Holiday;
	onDelete: (id: string) => void;
}

function DeleteHolidayAction({ holiday, onDelete }: DeleteActionProps) {
	const { t } = useTranslation("schedules");

	return (
		<PermissionGate resource="schedules" action="delete">
			<Popconfirm
				title={t("holidaysPage.delete.title")}
				description={t("holidaysPage.delete.description")}
				okText={t("holidaysPage.delete.ok")}
				cancelText={t("holidaysPage.delete.cancel")}
				okButtonProps={{ danger: true }}
				onConfirm={() => onDelete(holiday.id)}
			>
				<Button
					type="text"
					danger
					icon={<DeleteOutlined />}
					title={t("holidaysPage.delete.button")}
				/>
			</Popconfirm>
		</PermissionGate>
	);
}

export function HolidaysPage() {
	const { t } = useTranslation("schedules");
	const [year, setYear] = useState(dayjs().year());
	const [formOpen, setFormOpen] = useState(false);

	const { data: holidays = [], isLoading, isError } = useHolidaysList(year);
	const createHoliday = useCreateHoliday();
	const deleteHoliday = useDeleteHoliday();

	const handleSubmit = (input: HolidayInput) => {
		createHoliday.mutate(input, { onSuccess: () => setFormOpen(false) });
	};

	const columns: ColumnsType<Holiday> = [
		{ title: t("holidaysPage.columns.date"), dataIndex: "sana", key: "sana" },
		{ title: t("holidaysPage.columns.name"), dataIndex: "nom", key: "nom" },
		{
			title: t("holidaysPage.columns.type"),
			dataIndex: "tur",
			key: "tur",
			render: (tur: string | null) => tur ?? emptyCell,
		},
		{
			title: t("holidaysPage.columns.dayStatus"),
			dataIndex: "isWorkday",
			key: "isWorkday",
			render: (isWorkday: boolean) =>
				isWorkday ? (
					<Tag color="blue">{t("holidaysPage.workday")}</Tag>
				) : (
					<Tag color="red">{t("holidaysPage.restDay")}</Tag>
				),
		},
		{
			title: t("holidaysPage.columns.actions"),
			key: "actions",
			align: "right",
			render: (_, holiday) => (
				<DeleteHolidayAction holiday={holiday} onDelete={(id) => deleteHoliday.mutate(id)} />
			),
		},
	];

	return (
		<div>
			<div className="mb-6 flex items-center justify-between gap-4 flex-wrap">
				<Title level={3} className="!mb-0">
					{t("holidaysPage.title")}
				</Title>
				<PermissionGate resource="schedules" action="create">
					<Button type="primary" icon={<PlusOutlined />} onClick={() => setFormOpen(true)}>
						{t("holidaysPage.new")}
					</Button>
				</PermissionGate>
			</div>

			<Alert
				className="mb-4"
				type="info"
				showIcon
				title={t("holidaysPage.info.title")}
				description={t("holidaysPage.info.description")}
			/>

			{isError && (
				<Alert
					className="mb-4"
					type="error"
					showIcon
					title={t("holidaysPage.loadError")}
					description={t("holidaysPage.loadErrorDescription")}
				/>
			)}

			<Space wrap className="mb-4">
				<Select value={year} options={yearOptions} onChange={setYear} style={{ width: 120 }} />
			</Space>

			<Table<Holiday>
				columns={columns}
				dataSource={holidays}
				rowKey="id"
				loading={isLoading}
				pagination={false}
				scroll={{ x: "max-content" }}
			/>

			<HolidayFormModal
				open={formOpen}
				year={year}
				isSubmitting={createHoliday.isPending}
				onCancel={() => setFormOpen(false)}
				onSubmit={handleSubmit}
			/>
		</div>
	);
}
